import React from "react";
import {
	Typography,
	Image,
	Tree,
	Tabs,
	Tooltip,
	Table,
	Tag,
	Divider,
	Modal,
	Avatar,
} from "antd";
import {
	CheckCircleFilled,
	CheckCircleOutlined,
	CloseCircleOutlined,
	LockOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { capitalize } from "lodash";
import dayjs from "dayjs";
import {
	Fuson360SetupDataExport,
	Operation,
	SetupData,
	Tool,
} from "../../interfaces/Fusion360SetupData.interfaces";
import { ColumnsType } from "antd/es/table";
import { JawAssemblyConfiguration } from "../../interfaces/VersaBuilt.Interface";
import Fusion360Button from "../Fusion360Button";
import {
	isPartMachiningSetup,
	orderSetupsForDisplay,
	workholdingBySetup,
} from "./setupWorkholding";
import WorkholdingDetails from "./WorkholdingDetails";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

function mmToInch(value: number) {
	return value / 25.4;
}

function formatTime(totalSeconds: number) {
	const roundedSeconds = Math.round(totalSeconds);
	const hours = Math.floor(roundedSeconds / 3600);
	const minutes = Math.floor((roundedSeconds % 3600) / 60);
	const seconds = roundedSeconds % 60;
	const parts: string[] = [];
	if (hours > 0) {
		parts.push(`${hours} hrs.`);
	}
	if (minutes > 0) {
		parts.push(`${minutes} min.`);
	}
	if (seconds > 0 || parts.length === 0) {
		parts.push(`${seconds} sec.`);
	}
	return parts.join(" ");
}

function toBase64ImageSrc(imageValue?: string) {
	if (!imageValue) {
		return "";
	}
	return imageValue.startsWith("data:image")
		? imageValue
		: `data:image/png;base64,${imageValue}`;
}

interface ToolNumberAvatarColor {
	toolNumber: string;
	backgroundColor: string;
	borderColor: string;
	textColor?: string;
}

// Manually map tool numbers to avatar colors. Add/edit entries as needed.
const TOOL_NUMBER_AVATAR_COLORS: ToolNumberAvatarColor[] = [
	{ toolNumber: "1", backgroundColor: "hsl(0, 78%, 90%)", borderColor: "hsl(0, 86%, 52%)", textColor: "#111111" },
	{ toolNumber: "2", backgroundColor: "hsl(137, 66%, 86%)", borderColor: "hsl(137, 74%, 48%)", textColor: "#111111" },
	{ toolNumber: "3", backgroundColor: "hsl(274, 84%, 92%)", borderColor: "hsl(274, 92%, 54%)", textColor: "#111111" },
	{ toolNumber: "4", backgroundColor: "hsl(51, 72%, 78%)", borderColor: "hsl(51, 80%, 40%)", textColor: "#111111" },
	{ toolNumber: "5", backgroundColor: "hsl(188, 88%, 84%)", borderColor: "hsl(188, 96%, 46%)", textColor: "#111111" },
	{ toolNumber: "6", backgroundColor: "hsl(325, 78%, 90%)", borderColor: "hsl(325, 86%, 52%)", textColor: "#111111" },
	{ toolNumber: "7", backgroundColor: "hsl(102, 66%, 86%)", borderColor: "hsl(102, 74%, 48%)", textColor: "#111111" },
	{ toolNumber: "8", backgroundColor: "hsl(239, 84%, 92%)", borderColor: "hsl(239, 92%, 54%)", textColor: "#111111" },
	{ toolNumber: "9", backgroundColor: "hsl(16, 72%, 78%)", borderColor: "hsl(16, 80%, 40%)", textColor: "#111111" },
	{ toolNumber: "10", backgroundColor: "hsl(153, 88%, 84%)", borderColor: "hsl(153, 96%, 46%)", textColor: "#111111" },
	{ toolNumber: "11", backgroundColor: "hsl(290, 78%, 90%)", borderColor: "hsl(290, 86%, 52%)", textColor: "#111111" },
	{ toolNumber: "12", backgroundColor: "hsl(67, 66%, 86%)", borderColor: "hsl(67, 74%, 48%)", textColor: "#111111" },
	{ toolNumber: "13", backgroundColor: "hsl(204, 84%, 92%)", borderColor: "hsl(204, 92%, 54%)", textColor: "#111111" },
	{ toolNumber: "14", backgroundColor: "hsl(341, 72%, 78%)", borderColor: "hsl(341, 80%, 40%)", textColor: "#111111" },
	{ toolNumber: "15", backgroundColor: "hsl(118, 88%, 84%)", borderColor: "hsl(118, 96%, 46%)", textColor: "#111111" },
	{ toolNumber: "16", backgroundColor: "hsl(255, 78%, 90%)", borderColor: "hsl(255, 86%, 52%)", textColor: "#111111" },
	{ toolNumber: "17", backgroundColor: "hsl(32, 66%, 86%)", borderColor: "hsl(32, 74%, 48%)", textColor: "#111111" },
	{ toolNumber: "18", backgroundColor: "hsl(169, 84%, 92%)", borderColor: "hsl(169, 92%, 54%)", textColor: "#111111" },
	{ toolNumber: "19", backgroundColor: "hsl(306, 72%, 78%)", borderColor: "hsl(306, 80%, 40%)", textColor: "#111111" },
	{ toolNumber: "20", backgroundColor: "hsl(83, 88%, 84%)", borderColor: "hsl(83, 96%, 46%)", textColor: "#111111" },
	{ toolNumber: "21", backgroundColor: "hsl(220, 78%, 90%)", borderColor: "hsl(220, 86%, 52%)", textColor: "#111111" },
	{ toolNumber: "22", backgroundColor: "hsl(357, 66%, 86%)", borderColor: "hsl(357, 74%, 48%)", textColor: "#111111" },
	{ toolNumber: "23", backgroundColor: "hsl(134, 84%, 92%)", borderColor: "hsl(134, 92%, 54%)", textColor: "#111111" },
	{ toolNumber: "24", backgroundColor: "hsl(271, 72%, 78%)", borderColor: "hsl(271, 80%, 40%)", textColor: "#111111" },
	{ toolNumber: "25", backgroundColor: "hsl(48, 88%, 84%)", borderColor: "hsl(48, 96%, 46%)", textColor: "#111111" },
	{ toolNumber: "26", backgroundColor: "hsl(185, 78%, 90%)", borderColor: "hsl(185, 86%, 52%)", textColor: "#111111" },
	{ toolNumber: "27", backgroundColor: "hsl(322, 66%, 86%)", borderColor: "hsl(322, 74%, 48%)", textColor: "#111111" },
	{ toolNumber: "28", backgroundColor: "hsl(99, 84%, 92%)", borderColor: "hsl(99, 92%, 54%)", textColor: "#111111" },
	{ toolNumber: "29", backgroundColor: "hsl(236, 72%, 78%)", borderColor: "hsl(236, 80%, 40%)", textColor: "#111111" },
	{ toolNumber: "30", backgroundColor: "hsl(13, 88%, 84%)", borderColor: "hsl(13, 96%, 46%)", textColor: "#111111" },
	{ toolNumber: "31", backgroundColor: "hsl(150, 78%, 90%)", borderColor: "hsl(150, 86%, 52%)", textColor: "#111111" },
	{ toolNumber: "32", backgroundColor: "hsl(287, 66%, 86%)", borderColor: "hsl(287, 74%, 48%)", textColor: "#111111" },
	{ toolNumber: "33", backgroundColor: "hsl(64, 84%, 92%)", borderColor: "hsl(64, 92%, 54%)", textColor: "#111111" },
	{ toolNumber: "34", backgroundColor: "hsl(201, 72%, 78%)", borderColor: "hsl(201, 80%, 40%)", textColor: "#111111" },
	{ toolNumber: "35", backgroundColor: "hsl(338, 88%, 84%)", borderColor: "hsl(338, 96%, 46%)", textColor: "#111111" },
	{ toolNumber: "36", backgroundColor: "hsl(115, 78%, 90%)", borderColor: "hsl(115, 86%, 52%)", textColor: "#111111" },
	{ toolNumber: "37", backgroundColor: "hsl(252, 66%, 86%)", borderColor: "hsl(252, 74%, 48%)", textColor: "#111111" },
	{ toolNumber: "38", backgroundColor: "hsl(29, 84%, 92%)", borderColor: "hsl(29, 92%, 54%)", textColor: "#111111" },
	{ toolNumber: "39", backgroundColor: "hsl(166, 72%, 78%)", borderColor: "hsl(166, 80%, 40%)", textColor: "#111111" },
	{ toolNumber: "40", backgroundColor: "hsl(303, 88%, 84%)", borderColor: "hsl(303, 96%, 46%)", textColor: "#111111" },
	{ toolNumber: "41", backgroundColor: "hsl(80, 78%, 90%)", borderColor: "hsl(80, 86%, 52%)", textColor: "#111111" },
	{ toolNumber: "42", backgroundColor: "hsl(217, 66%, 86%)", borderColor: "hsl(217, 74%, 48%)", textColor: "#111111" },
	{ toolNumber: "43", backgroundColor: "hsl(354, 84%, 92%)", borderColor: "hsl(354, 92%, 54%)", textColor: "#111111" },
	{ toolNumber: "44", backgroundColor: "hsl(131, 72%, 78%)", borderColor: "hsl(131, 80%, 40%)", textColor: "#111111" },
	{ toolNumber: "45", backgroundColor: "hsl(268, 88%, 84%)", borderColor: "hsl(268, 96%, 46%)", textColor: "#111111" },
	{ toolNumber: "46", backgroundColor: "hsl(45, 78%, 90%)", borderColor: "hsl(45, 86%, 52%)", textColor: "#111111" },
	{ toolNumber: "47", backgroundColor: "hsl(182, 66%, 86%)", borderColor: "hsl(182, 74%, 48%)", textColor: "#111111" },
	{ toolNumber: "48", backgroundColor: "hsl(319, 84%, 92%)", borderColor: "hsl(319, 92%, 54%)", textColor: "#111111" },
	{ toolNumber: "49", backgroundColor: "hsl(96, 72%, 78%)", borderColor: "hsl(96, 80%, 40%)", textColor: "#111111" },
	{ toolNumber: "50", backgroundColor: "hsl(233, 88%, 84%)", borderColor: "hsl(233, 96%, 46%)", textColor: "#111111" },
];

function getToolNumberAvatarStyle(toolNumber: string): React.CSSProperties {
	if (!toolNumber || toolNumber === "-") {
		return {
			backgroundColor: "#f2f2f2",
			color: "#262626",
			border: "2px solid #8c8c8c",
			fontSize: "14px",
		};
	}

	const toolNumberKey = String(toolNumber).trim();
	const mappedColor = TOOL_NUMBER_AVATAR_COLORS.find(
		(item) => item.toolNumber === toolNumberKey
	);

	if (!mappedColor) {
		return {
			backgroundColor: "#f2f2f2",
			color: "#262626",
			border: "2px solid #8c8c8c",
			fontSize: "14px",
		};
	}

	return {
		backgroundColor: mappedColor.backgroundColor,
		color: mappedColor.textColor || "#111111",
		border: `2px solid ${mappedColor.borderColor}`,
		fontSize: "14px",
	};
}

const OperationStrategyLookup = [
	{ key: "adaptive", label: "Adaptive" },
	{ key: "adaptive2d", label: "Adaptive 2D" },
	{ key: "blend", label: "Blend" },
	{ key: "bore", label: "Bore" },
	{ key: "chamfer2d", label: "Chamfer 2D" },
	{ key: "circular", label: "Circular" },
	{ key: "contour2d", label: "Contour 2D" },
	{ key: "pocket2d", label: "Pocket 2D" },
	{ key: "drill", label: "Drill" },
	{ key: "engrave", label: "Engrave" },
	{ key: "face", label: "Face" },
	{ key: "feature_construction", label: "Feature Construction" },
	{ key: "flat", label: "Flat" },
	{ key: "flow", label: "Flow" },
	{ key: "flow2", label: "Flow 2" },
	{ key: "folder", label: "Folder" },
	{ key: "hole_recognition", label: "Hole Recognition" },
	{ key: "inclined_walls", label: "Inclined Walls" },
	{ key: "inspect_surface", label: "Inspect Surface" },
	{ key: "profile2d", label: "Profile 2D" },
	{ key: "manual", label: "Manual" },
	{ key: "rotary_contour", label: "Rotary Contour" },
	{ key: "rotary_pocket", label: "Rotary Pocket" },
	{ key: "deburr", label: "Deburr" },
	{ key: "geodesic", label: "Geodesic" },
	{ key: "multiaxis_finishing", label: "Multiaxis Finishing" },
	{ key: "multiaxis_roughing", label: "Multiaxis Roughing" },
	{ key: "advanced_swarf", label: "Advanced Swarf" },
	{ key: "three_plus_two", label: "Three Plus Two" },
	{ key: "morph", label: "Morph" },
	{ key: "morphed_spiral", label: "Morphed Spiral" },
	{ key: "multi_axis_contour", label: "Multi Axis Contour" },
	{ key: "multi_axis_morph", label: "Multi Axis Morph" },
	{ key: "contour3d", label: "Contour 3D" },
	{ key: "pocket_clearing", label: "Pocket Clearing" },
	{ key: "horizontal", label: "Horizontal" },
	{ key: "parallel", label: "Parallel" },
	{ key: "pencil", label: "Pencil" },
	{ key: "radial", label: "Radial" },
	{ key: "scallop", label: "Scallop" },
	{ key: "spiral", label: "Spiral" },
	{ key: "probe", label: "Probe" },
	{ key: "probe_geometry", label: "Probe Geometry" },
	{ key: "project", label: "Project" },
	{ key: "ramp", label: "Ramp" },
	{ key: "corner", label: "Corner" },
	{ key: "rotary_finishing", label: "Rotary Finishing" },
	{ key: "slot", label: "Slot" },
	{ key: "steep_and_shallow", label: "Steep And Shallow" },
	{ key: "swarf", label: "Swarf" },
	{ key: "thread", label: "Thread" },
	{ key: "trace", label: "Trace" },
];

const getStrategyLabel = (strategyKey: string) => {
	const entry = OperationStrategyLookup.find(
		(item) => item.key === strategyKey
	);
	return entry ? entry.label : strategyKey;
};

function getNestedValue<T>(obj: T, keyPath: string): any {
	return keyPath
		.split(".")
		.reduce((o, k) => (o ? (o as any)[k] : undefined), obj);
}

interface TreeNode {
	title: React.ReactNode;
	key: string;
	children?: TreeNode[];
}

interface Props {
	data: SetupData;
	fusion_360_document_id: string;
	setup_count: string;
	setup_1_vise?: string;
	setup_1_jaw_assembly_configuration?: JawAssemblyConfiguration;
	setup_2_vise?: string;
	setup_2_jaw_assembly_configuration?: JawAssemblyConfiguration;
	setup_3_vise?: string;
	setup_3_jaw_assembly_configuration?: JawAssemblyConfiguration;
}

const Fusion360SetupSheet = (props: Props) => {
	const { data } = props;
	const { setups: rawSetups, ncPrograms, tools } = data;
	const setups = orderSetupsForDisplay(rawSetups ?? []);

	const setupWorkholding = workholdingBySetup(
		setups.map((setup) => setup?.name),
		[
			{
				jaw: props.setup_1_jaw_assembly_configuration ?? null,
				vise: props.setup_1_vise ?? null,
			},
			{
				jaw: props.setup_2_jaw_assembly_configuration ?? null,
				vise: props.setup_2_vise ?? null,
			},
			{
				jaw: props.setup_3_jaw_assembly_configuration ?? null,
				vise: props.setup_3_vise ?? null,
			},
		]
	);

	const partSetups = setups
		.map((setup, index) => ({ setup, workholding: setupWorkholding[index] }))
		.filter(({ setup }) => isPartMachiningSetup(setup?.name));

	const standardTools = tools?.filter(
		(tool) => Number(tool["post-process"].number) >= 9
	);
	const nonStandardTools = tools?.filter(
		(tool) => Number(tool["post-process"].number) < 9
	);

	function cmToIn(value: number) {
		return Number(Number(value / 2.54).toFixed(3));
	}

	function formatWorkOffset(offset: number) {
		if (offset === 0 || offset === 1) {
			return "G54";
		}
		if (offset === 2) {
			return "G55";
		}
		if (offset === 3) {
			return "G56";
		}
		if (offset > 3) {
			return `G154 P${offset - 6}`;
		}
	}

	return (
		<>
			<div className="w-full flex flex-col gap-3">
				<div>
					<div className="flex flex-col lg:flex-row gap-4">
						<div className="w-full">
							<Title level={4}>Setups</Title>
							<Tabs
								defaultActiveKey={
									setups && setups.length > 0 ? setups[0].name : ""
								}
							>
								{setups.map((setup) => {
									return (
										<TabPane
											tab={setup?.name || "Unknown Setup"}
											key={setup?.name || "unknown"}
										>
											<div className="flex flex-col gap-10 flex-nowrap">
												<div className="flex flex-col md:flex-row gap-10">
													{setup?.image && (
														<div className="min-w-96">
															<Image
																src={`data:image/png;base64,${setup.image}`}
																alt={`${setup?.name || "Setup"} Screenshot`}
																className="border-0"
																style={{ maxWidth: "480px" }}
															/>
														</div>
													)}
													<div className="flex flex-col gap-5 min-w-64">
														<Title level={5}>Details</Title>
														<div>
															{setup?.parameters?.job_type && (
																<p>
																	<Text strong>Job Type: </Text>
																	{setup?.parameters.job_type.expression ===
																		"'milling'"
																		? "Milling"
																		: setup?.parameters.job_type.expression ===
																			"'turning'"
																			? "Turning"
																			: setup?.parameters.job_type.expression}
																</p>
															)}
															{setup?.parameters?.job_workOffset && (
																<p>
																	<Text strong>Work Offset: </Text>
																	{formatWorkOffset(
																		Number(
																			setup?.parameters.job_workOffset.value
																		)
																	)}
																</p>
															)}
															{setup?.parameters?.job_stockInfoDimensionX && (
																<p>
																	<Text strong>Stock Size X: </Text>
																	{cmToIn(
																		setup?.parameters.job_stockInfoDimensionX
																			.value
																	)}
																</p>
															)}
															{setup?.parameters?.job_stockInfoDimensionY && (
																<p>
																	<Text strong>Stock Size Y: </Text>
																	{cmToIn(
																		setup?.parameters.job_stockInfoDimensionY
																			.value
																	)}
																</p>
															)}
															{setup?.parameters?.job_stockInfoDimensionZ && (
																<p>
																	<Text strong>Stock Size Z: </Text>
																	{cmToIn(
																		setup?.parameters.job_stockInfoDimensionZ
																			.value
																	)}
																</p>
															)}
															{setup?.machiningTime?.machiningTime && (
																<p>
																	<Text strong>Total Machining Time: </Text>
																	{formatTime(
																		setup?.machiningTime?.machiningTime
																	)}
																</p>
															)}
														</div>
														<Title level={5}>Machine</Title>
														<div>
															{setup?.machine?.vendor && (
																<p>
																	<Text strong>Vendor: </Text>
																	{setup?.machine.vendor}
																</p>
															)}
															{setup?.machine?.model && (
																<p>
																	<Text strong>Model: </Text>
																	{setup?.machine.model}
																</p>
															)}
															{setup?.machine?.description && (
																<p>
																	<Text strong>Description: </Text>
																	{setup?.machine.description}
																</p>
															)}
														</div>
													</div>
												</div>
												<div className="w-full">
													{setup?.operations?.length > 0 && (
														<div className="w-full">
															<Title level={4}>Operations</Title>
															<SetupOperationsTable
																operations={setup?.operations}
															/>
														</div>
													)}
												</div>
											</div>
										</TabPane>
									);
								})}
							</Tabs>

							{partSetups.length > 0 && (
								<>
									<Divider />

									<div className="w-full">
										<Title level={4}>Jaws</Title>
										<Tabs
											items={partSetups.map(({ setup, workholding }, index) => ({
												key: setup?.name || `setup-${index}`,
												label: setup?.name || "Unknown Setup",
												children: <WorkholdingDetails workholding={workholding} />,
											}))}
										/>
									</div>
								</>
							)}

							<Divider />

							<div className="w-full">
								<Title level={4}>Tools</Title>
								<Tabs
									defaultActiveKey="Non-Standard"
									items={[
										{
											key: "All",
											label: `All (${tools.length})`,
											children: <ToolDataTable tools={tools} />,
										},
										{
											key: "Standard",
											label: `Standard (${standardTools.length})`,
											children: <ToolDataTable tools={standardTools} />,
										},
										{
											key: "Non-Standard",
											label: `Non-Standard (${nonStandardTools.length})`,
											children: <ToolDataTable tools={nonStandardTools} />,
										},
									]}
								/>
							</div>

							<Divider />

							<Title level={4}>NC Programs</Title>
							<Tabs>
								{ncPrograms.map((ncProgram) => (
									<TabPane tab={ncProgram?.name} key={ncProgram?.name}>
										<div className="flex flex-row gap-10">
											<div className="flex flex-col gap-4">
												{ncProgram?.ncProgramParameters?.nc_program_name && (
													<p className="flex flex-col">
														<Text strong>NC Program Name: </Text>
														<Text copyable={true}>
															{
																ncProgram?.ncProgramParameters?.nc_program_name
																	.value
															}
														</Text>
													</p>
												)}
												{ncProgram?.ncProgramParameters
													?.nc_program_filename && (
														<p className="flex flex-col">
															<Text strong>NC Program File Name: </Text>
															<Text copyable={true}>
																{
																	ncProgram?.ncProgramParameters
																		?.nc_program_filename.value
																}
															</Text>
														</p>
													)}
												{ncProgram?.ncProgramParameters
													?.nc_program_output_folder && (
														<p className="flex flex-col">
															<Text strong>NC Output Path: </Text>
															<Text copyable={true}>
																{
																	ncProgram?.ncProgramParameters
																		?.nc_program_output_folder.value
																}
															</Text>
														</p>
													)}
												{ncProgram?.ncProgramParameters
													?.nc_program_useMachineConfig &&
													ncProgram?.ncProgramParameters
														?.nc_program_useMachineConfig.value === true && (
														<p className="flex flex-col items-start">
															<Tag color="green" icon={<CheckCircleFilled />}>
																Machine Config
															</Tag>
														</p>
													)}
											</div>
											<div className="flex flex-col gap-4">
												{ncProgram?.postProcessor?.description && (
													<p className="flex flex-col">
														<Text strong>Post Processor: </Text>
														<Text>{ncProgram?.postProcessor?.description}</Text>
														<Text>
															Version {ncProgram?.postProcessor?.version}
														</Text>
													</p>
												)}
											</div>
											<div>
												{ncProgram?.operations?.length > 0 && (
													<div>
														<Title level={5}>Operations</Title>
														<OperationsTree
															operations={ncProgram?.operations}
														/>
													</div>
												)}
											</div>
										</div>
									</TabPane>
								))}
							</Tabs>
						</div>
					</div>
					<div className="flex flex-row justify-between items-baseline">
						<div className="flex flex-row italic gap-1 pt-5">
							<span>{`Exported from Fusion 360 on`}</span>
							<span>{`${dayjs(data?.created_at).format(
								"MM-DD-YY h:mm a"
							)} by`}</span>
							<span>{data?.created_by?.displayName}</span>
						</div>
						{props.fusion_360_document_id ? (
							<Fusion360Button fusionURN={props.fusion_360_document_id} />
						) : null}
					</div>
				</div>
			</div>
		</>
	);
};

interface ToolsProps {
	tools: Tool[];
}

interface ToolRecord extends Tool {
	key: React.Key;
}

const ToolDataTable = (props: ToolsProps) => {
	const { tools } = props;

	const columns: ColumnsType<ToolRecord> = [
		{
			title: "#",
			key: "toolNumber",
			width: 70,
			sorter: (a: ToolRecord, b: ToolRecord) => {
				const numA = parseFloat(getNestedValue(a, "post-process.number")) || 0;
				const numB = parseFloat(getNestedValue(b, "post-process.number")) || 0;
				return numA - numB;
			},
			render: (record: Tool) => {
				const toolNumber = getNestedValue(record, "post-process.number") || 0;
				return <Text strong>{toolNumber}</Text>;
			},
			defaultSortOrder: "ascend",
		},
		{
			title: "Description",
			key: "description",
			width: 500,
			render: (record: Tool) => (
				<div className="flex flex-col">
					<Text>{record.description}</Text>
					<div className="flex flex-row gap-2">
						<Text type="secondary">{capitalize(record.type)}</Text>
					</div>
				</div>
			),
		},
		{
			title: "Cutting Diameter",
			key: "cuttingDiameter",
			width: 150,
			render: (record: Tool) => (
				<>
					<span>
						{record.unit === "inches"
							? Number(record.geometry.DC).toFixed(4) + " in."
							: record.unit === "millimeters"
								? Number(record.geometry.DC).toFixed(2) + " mm"
								: null}
					</span>
				</>
			),
		},
		{
			title: "Shank Diameter",
			key: "shankDiameter",
			width: 150,
			render: (record: Tool) => (
				<>
					<span>
						{record.unit === "inches"
							? Number(record.geometry.SFDM).toFixed(4) + " in."
							: record.unit === "millimeters"
								? Number(record.geometry.SFDM).toFixed(2) + " mm"
								: null}
					</span>
				</>
			),
		},
		{
			title: "Vendor",
			key: "vendor",
			width: 200,
			render: (record: Tool) => <Text>{record.vendor}</Text>,
		},
		{
			title: "Item",
			key: "product-id",
			width: 250,
			render: (record: Tool) =>
				record["product-id"] ? (
					<Text copyable={{ text: record["product-id"] }}>
						{record["product-id"]}
					</Text>
				) : null,
		},
		{
			title: "Link",
			key: "product-link",
			ellipsis: true,
			width: 250,
			render: (record: Tool) => (
				<a
					href={record["product-link"]}
					target="_blank"
					rel="noopener noreferrer"
				>
					{record["product-link"]}
				</a>
			),
		},

		{
			title: "Holder",
			key: "holder.description",
			width: 250,
			render: (record: Tool) =>
				record?.holder ? (
					<>
						<Text copyable={{ text: record.holder.description }}>
							{record.holder.description}
						</Text>
					</>
				) : null,
		},
		{
			title: "Gauge Length",
			key: "geometry.assemblyGaugeLength",
			width: 150,
			render: (record: Tool) => (
				<Text>
					{`${Number(
						record.unit === "inches"
							? record.geometry.assemblyGaugeLength
							: mmToInch(record.geometry.assemblyGaugeLength)
					).toFixed(3)} in.`}
				</Text>
			),
		},
		{
			title: "Length below holder",
			key: "geometry.LB",
			width: 200,
			render: (record: Tool) => (
				<>{`${Number(
					record.unit === "inches"
						? record.geometry.LB
						: mmToInch(record.geometry.LB)
				).toFixed(3)} in.`}</>
			),
		},
		{
			title: "Break Control",
			key: "post-process.break-control",
			width: 150,
			render: (record: Tool) => (
				<Text>
					{getNestedValue(record, "post-process.break-control") ? (
						<CheckCircleOutlined style={{ color: "green" }} />
					) : (
						<WarningOutlined style={{ color: "orange" }} />
					)}
				</Text>
			),
		},
		{
			title: "Unit",
			key: "unit",
			width: 100,
			render: (record: Tool) => <Text>{capitalize(record.unit)}</Text>,
		},
		{
			key: "hasMultiAxisOperations",
			width: 70,
			render: (record: Tool) => (
				<Text>
					{record.hasMultiAxisOperations ? (
						<Tag color="magenta-inverse">3+2</Tag>
					) : null}
				</Text>
			),
		},
	];

	const dataSource: ToolRecord[] =
		tools?.map((tool, index) => ({
			...tool,
			key: index,
		})) || [];

	return (
		<Table
			dataSource={dataSource}
			columns={columns}
			pagination={false}
			scroll={{ x: "1920px" }}
		/>
	);
};

interface OperationsTreeProps {
	operations: Operation[];
}

interface SetupOperationRecord extends Operation {
	key: React.Key;
	imageSrc?: string;
	toolLabel: string;
	toolNumber: string;
	toolType: string;
	operationType: string;
	cycleTime: string;
	hasWearComp: boolean;
}

const SetupOperationsTable = (props: OperationsTreeProps) => {
	const { operations } = props;

	const columns: ColumnsType<SetupOperationRecord> = [
		{
			title: "Image",
			key: "image",
			width: 90,
			render: (record: SetupOperationRecord) =>
				record.imageSrc ? (
					<Image
						src={record.imageSrc}
						alt={`${record?.name || "Operation"} thumbnail`}
						preview={{ src: record.imageSrc }}
						width={48}
						height={48}
						style={{ objectFit: "cover", borderRadius: "4px" }}
					/>
				) : <div className="w-12 h-12 bg-gray-200 rounded-md text-center flex items-center justify-center">No Image</div>,
		},
		{
			title: "Operation",
			key: "operationName",
			width: 300,

			render: (record: SetupOperationRecord) => (
				<Text strong>{record?.name || "Unnamed Operation"}</Text>
			),
		},
		{
			title: "Type",
			key: "operationType",
			width: 180,
			render: (record: SetupOperationRecord) => <Text>{record.operationType}</Text>,
		},
		{
			title: "Cycle Time",
			key: "cycleTime",
			width: 140,
			render: (record: SetupOperationRecord) => <Text>{record.cycleTime}</Text>,
		},
		{
			title: "Tool",
			key: "tool",
			width: 320,
			render: (record: SetupOperationRecord) => (
				<span className="flex flex-row items-center gap-2">
					<Avatar
						// size="small"
						shape="square"
						style={getToolNumberAvatarStyle(record.toolNumber)}
					>
						{record.toolNumber}
					</Avatar>
					<Text>{record.toolLabel}</Text>
				</span>
			),
		},
		{
			title: "Tool Type",
			key: "toolType",
			width: 180,
			render: (record: SetupOperationRecord) => <Text>{record.toolType}</Text>,
		},
		{
			title: "Wear Comp",
			key: "wearComp",
			width: 140,
			render: (record: SetupOperationRecord) =>
				record.hasWearComp ? <Tag color="cyan-inverse">Wear Comp.</Tag> : "-",
		},
	];

	const dataSource: SetupOperationRecord[] =
		operations?.map((op, index) => {
			const operationImage = getNestedValue(op as any, "image") as
				| string
				| undefined;
			const imageSrc = operationImage
				? toBase64ImageSrc(operationImage)
				: undefined;
			const strategyLabel = op?.strategy ? getStrategyLabel(op.strategy) : "";
			const operationType =
				(getNestedValue(op as any, "type") as string) || strategyLabel || "-";
			const cycleTimeSeconds = op?.operationMachiningTime?.machiningTime;
			const cycleTime =
				typeof cycleTimeSeconds === "number" ? formatTime(cycleTimeSeconds) : "-";

			let toolLabel = "N/A";
			let toolNumber = "-";
			let toolType = "-";
			if (op?.tool) {
				try {
					const toolData = typeof op.tool === "string" ? JSON.parse(op.tool) : op.tool;
					const parsedToolNumber = getNestedValue(toolData, "post-process.number");
					const toolDescription = getNestedValue(toolData, "description");
					toolType = capitalize(String(getNestedValue(toolData, "type") || "-"));
					toolNumber = parsedToolNumber != null ? String(parsedToolNumber) : "-";
					toolLabel = toolDescription || "Unknown Tool";
				} catch (error) {
					console.error("Error parsing operation tool data", error);
				}
			}

			return {
				...op,
				key: `${op?.operationId ?? "operation"}-${index}`,
				imageSrc,
				toolLabel,
				toolNumber,
				toolType,
				operationType,
				cycleTime,
				hasWearComp: op?.parameters?.compensationType?.expression === "'wear'",
			};
		}) || [];

	return (
		<>
			<Table
				dataSource={dataSource}
				columns={columns}
				pagination={false}
				scroll={{ x: "1000px" }}
				size="small"
			/>
		</>
	);
};

const OperationsTree = (props: OperationsTreeProps) => {
	const [previewImageSrc, setPreviewImageSrc] = React.useState<string>("");
	const [previewImageTitle, setPreviewImageTitle] = React.useState<string>("");
	const desiredKeys: { label: string; key: string }[] = [
		{ label: "Number", key: "post-process.number" },
		{ label: "Type", key: "type" },
		{ label: "Description", key: "description" },
		{ label: "Vendor", key: "vendor" },
		{ label: "Unit", key: "unit" },
		{ label: "Holder", key: "holder.description" },
	];

	return (
		<Tree
			selectable={false}
			showLine
			treeData={props.operations.map((op, opIndex) => {
				const operationImage = getNestedValue(op as any, "image") as
					| string
					| undefined;
				const strategyLabel = op?.strategy ? getStrategyLabel(op.strategy) : "";
				let icon;
				if (op?.isSuppressed) {
					icon = (
						<CloseCircleOutlined
							style={{
								color: "grey",
								marginRight: 4,
							}}
						/>
					);
				} else if (op?.isProtected) {
					icon = (
						<LockOutlined
							style={{
								color: "grey",
								marginRight: 4,
							}}
						/>
					);
				} else if (op?.hasWarning) {
					icon = (
						<Tooltip title={op?.warning || ""}>
							<WarningOutlined
								style={{
									color: "orange",
									marginRight: 4,
								}}
							/>
						</Tooltip>
					);
				} else if (op?.hasError) {
					icon = (
						<Tooltip title={op?.error || ""}>
							<CloseCircleOutlined
								style={{
									color: "red",
									marginRight: 4,
								}}
							/>
						</Tooltip>
					);
				} else {
					icon = (
						<CheckCircleOutlined
							style={{
								color: "green",
								marginRight: 4,
							}}
						/>
					);
				}
				const opTitle = (
					<span className="flex flex-row gap-2 items-center">
						{icon}
						{op?.name || "Unnamed Operation"}
						{strategyLabel ? ` (${strategyLabel})` : ""}
						{op?.parameters?.overrideToolView?.value === true ? (
							<Tag color="magenta-inverse">3+2</Tag>
						) : null}
						{op?.parameters?.compensationType?.expression === "'wear'" ? (
							<Tag color="cyan-inverse">Wear Comp.</Tag>
						) : null}
					</span>
				);
				const opDetails = [
					{
						label: "Strategy",
						value: strategyLabel || "N/A",
						key: `operation-${opIndex}-strategy`,
					},
					{
						label: "Is Valid",
						value: op?.isValid ? "Yes" : "No",
						key: `operation-${opIndex}-isValid`,
					},
					{
						label: "Compensation Type",
						value: op?.parameters?.compensationType?.expression || "N/A",
						key: `operation-${opIndex}-compensationType`,
					},
					{
						label: "Machining Time",
						value: formatTime(op?.operationMachiningTime?.machiningTime),
						key: `operation-${opIndex}-machiningTime`,
					},
				];
				const opChildren: TreeNode[] = opDetails.map((detail) => ({
					title: (
						<span>
							<Text strong>{detail.label}: </Text>
							<Text>{detail.value}</Text>
						</span>
					),
					key: detail.key,
				}));
				if (op?.tool) {
					const toolData =
						typeof op.tool === "string" ? JSON.parse(op.tool) : op.tool;
					const toolChildren = desiredKeys
						.filter((item) => getNestedValue(toolData, item.key) != null)
						.map((item) => ({
							title: (
								<span>
									<Text strong>{item.label}: </Text>
									<Text>{getNestedValue(toolData, item.key)}</Text>
								</span>
							),
							key: `${op?.name || "unknown"}-${opIndex}-${item.key}`,
						}));
					opChildren.push({
						title: <Text strong>Tool Details</Text>,
						key: `operation-${opIndex}-tool`,
						children: toolChildren,
					});
				}
				if (operationImage) {
					opChildren.push({
						title: (
							<div className="flex flex-col gap-2">
								<Text strong>Image:</Text>
								<img
									src={toBase64ImageSrc(operationImage)}
									alt={`${op?.name || "Operation"} preview`}
									style={{
										maxWidth: "240px",
										cursor: "pointer",
										borderRadius: "4px",
									}}
									onClick={() => {
										setPreviewImageSrc(toBase64ImageSrc(operationImage));
										setPreviewImageTitle(op?.name || "Operation Preview");
									}}
								/>
							</div>
						),
						key: `operation-${opIndex}-image`,
					});
				}
				return {
					title: opTitle,
					key: `operation-${opIndex}`,
					children: opChildren,
				};
			})}
		>
			<Modal
				open={Boolean(previewImageSrc)}
				onCancel={() => setPreviewImageSrc("")}
				footer={null}
				width="100%"
				style={{ top: 0, paddingBottom: 0 }}
				title={previewImageTitle}
				styles={{
					body: {
						background: "#000",
						textAlign: "center",
						padding: "8px",
					},
				}}
			>
				{previewImageSrc ? (
					<img
						src={previewImageSrc}
						alt={previewImageTitle}
						style={{ maxWidth: "100%", maxHeight: "85vh", margin: "0 auto" }}
					/>
				) : null}
			</Modal>
		</Tree>
	);
};

interface Fusion360SetupDataProps {
	data: Fuson360SetupDataExport[];
}

const Fusion360SetupData = (props: Fusion360SetupDataProps) => {
	return (
		<Tabs
			type="card"
			size="small"
			tabBarStyle={{ marginBottom: 0 }}
			items={props.data?.map((item, index) => {
				return {
					key: item.machine
						? item.machine.id
						: item.machine_model
							? item.machine_model.id
							: String(index),
					label: item.machine
						? item.machine.name
						: item.machine_model
							? item.machine_model.model
							: `Setup ${index + 1}`,
					children: (
						<div className="w-full bg-white border-b border-r border-l p-6">
							<Fusion360SetupSheet
								data={item.cam_data}
								fusion_360_document_id={item.fusion_360_document_id}
								setup_count={item.setup_count}
								setup_1_vise={item?.setup_1_vise}
								setup_1_jaw_assembly_configuration={
									item?.setup_1_jaw_assembly_configuration
								}
								setup_2_vise={item?.setup_2_vise}
								setup_2_jaw_assembly_configuration={
									item?.setup_2_jaw_assembly_configuration
								}
								setup_3_vise={item?.setup_3_vise}
								setup_3_jaw_assembly_configuration={
									item?.setup_3_jaw_assembly_configuration
								}
							/>
						</div>
					),
				};
			})}
		/>
	);
};

export default Fusion360SetupData;
